// ─────────────────────────────────────────────────────────────────────────
// dayBriefingPrompt - builds the prompt for the industry-expert agent that
// writes the "plan your day" briefing over today's sequenced stops.
//
// This is the half that scales to ALL 73 industries with NO hardcoding: the
// static lexicon (industryLexicon.js) handles the fast UI nouns, and THIS
// hands the long-form prose to the backend agent, which already writes "in
// the rep's industry voice (wedge_prefs pack + lexicon)" - the same voice
// path the email composer uses. A pharma rep gets a pharma briefing; a
// marine-power rep gets a marine one; an industry we have never seen still
// gets a competent professional briefing because the agent is the expert,
// not this file.
//
// HONESTY CONTRACT baked into the system prompt (boardroom no-fake rule):
//   - Call it a SUGGESTED order. Never "optimized".
//   - NEVER state a drive time, an ETA, a distance, or a minutes-saved
//     number. The client sequencer does not know traffic; inventing a
//     figure is a fabricated capability.
//   - Booked appointments are fixed; free stops are proximity-suggested in
//     the gaps. Say so plainly when it matters.
//   - Ground every claim in the provided stop facts. Do not invent stops,
//     contacts, or events.
// ─────────────────────────────────────────────────────────────────────────

import { routeLexicon } from './industryLexicon.js';

const MAX_STOPS_IN_PROMPT = 25;

function fmtTime(ms) {
  if (!Number.isFinite(ms)) return null;
  try {
    return new Date(ms).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  } catch (e) {
    return null;
  }
}

/**
 * Build the {system, user} messages for the day-briefing agent.
 *
 * @param {Object} args
 * @param {import('./sequencer.js').Stop[]} args.order - stops in suggested order (from sequenceDay)
 * @param {import('./sequencer.js').Stop[]} [args.unplaceable] - stops missing coords (honest mention)
 * @param {string} [args.industryPrimary] - wedge_prefs.industry.primary
 * @param {string} [args.repName]
 * @param {string} [args.role] - wedge_prefs role, if captured in onboarding
 * @returns {{ system: string, user: string }}
 */
export function buildDayBriefingPrompt(args) {
  const { order = [], unplaceable = [], industryPrimary = '', repName = '', role = '' } = args || {};
  const lex = routeLexicon(industryPrimary);
  const industryLabel = industryPrimary || 'field sales';

  const system = [
    `You are RepVantage's day-planning analyst for a ${industryLabel} field rep${role ? ` (${role})` : ''}.`,
    `Write a short, punchy briefing that helps the rep run today's ${lex.sequenceNoun} of ${lex.stopPlural}.`,
    `Speak in the working vocabulary of the ${industryLabel} vertical: a place the rep visits is a "${lex.stopSingular}".`,
    '',
    'HARD RULES (do not break):',
    `- This is a SUGGESTED order by proximity. Never call it "optimized".`,
    '- Never state a drive time, ETA, distance, or minutes-saved number. You do not know traffic.',
    '- Booked appointments are fixed anchors; free stops are suggested in the gaps between them. Say so if relevant.',
    '- Ground every sentence in the stop facts provided. Never invent a stop, a person, or an event.',
    '- No em dashes. Plain, direct field-rep language.',
    '',
    `Output: 2 to 4 tight sentences the rep reads at 8:55am before pulling out. Lead with the first ${lex.stopSingular} and why it is first.`,
  ].join('\n');

  const lines = order.slice(0, MAX_STOPS_IN_PROMPT).map((s, i) => {
    const when = isFinitePin(s) ? ` [BOOKED ${fmtTime(s.apptStart) || 'today'}]` : '';
    const why = s.issue || s.note || s.topLine || '';
    return `${i + 1}. ${s.name || s.id || 'stop'}${when}${why ? ` - ${why}` : ''}`;
  });
  if (order.length > MAX_STOPS_IN_PROMPT) {
    lines.push(`(+${order.length - MAX_STOPS_IN_PROMPT} more ${lex.stopPlural} today)`);
  }

  const userParts = [
    repName ? `Rep: ${repName}` : '',
    `Today's suggested ${lex.sequenceNoun} (${order.length} ${order.length === 1 ? lex.stopSingular : lex.stopPlural}):`,
    lines.join('\n'),
  ];
  if (unplaceable.length) {
    userParts.push('', `${unplaceable.length} ${unplaceable.length === 1 ? lex.stopSingular : lex.stopPlural} have no confirmed location yet and are not in the order. Mention them once so the rep can fix the address.`);
  }
  userParts.push('', `Write the briefing.`);

  return { system, user: userParts.filter(Boolean).join('\n') };
}

function isFinitePin(s) {
  return s != null && Number.isFinite(s.apptStart);
}
